import { ArrowsDownUp, ChartLineUp, Gauge, Hash, Percent } from "@phosphor-icons/react"

import { matchStatusField } from "~/components/demand-2/shared/matchStatusFilter"
import { DEMAND2_PERF_WINDOWS } from "~/components/demand-2/shared/useDemand2Performance"
import { PERFORMANCE_STATUSES } from "~/components/demand-2/tabs/performanceStatus"
import { FORECAST_QUALITY_LABELS } from "~/constants/forecastEvaluation"
import type { FilterFieldGroup } from "~/components/planning/FilterFieldGroupList"
import type { FilterFieldType } from "~/utils/filter"
import { formatNumber, formatNumberWithSign } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"

export const FORECAST_QUALITY_FIELD = "forecastQualityLevel"
export const CHANGE_VS_PUBLISHED_FIELD = "changeVsPublishedPct"
export const CHANGE_VS_PUBLISHED_ABS_FIELD = "changeVsPublishedAbsPct"
export const CHANGE_VS_PUBLISHED_UNITS_FIELD = "changeVsPublishedUnits"
export const CHANGE_VS_PUBLISHED_ABS_UNITS_FIELD = "changeVsPublishedAbsUnits"
export const CHANGE_RULE_FIELDS: readonly string[] = [
  CHANGE_VS_PUBLISHED_FIELD,
  CHANGE_VS_PUBLISHED_ABS_FIELD,
  CHANGE_VS_PUBLISHED_UNITS_FIELD,
  CHANGE_VS_PUBLISHED_ABS_UNITS_FIELD,
]
/** The status grades the shortest shared window, so it exists whatever the window list holds. */
export const PERFORMANCE_STATUS_WEEKS = Math.min(...DEMAND2_PERF_WINDOWS)
export const PERFORMANCE_STATUS_FIELD = `performanceStatus${PERFORMANCE_STATUS_WEEKS}w`
/** The units behind the change, stamped beside it for the hover. */
export const CHANGE_DRAFT_UNITS = "changeVsPublishedDraftUnits"
export const CHANGE_PUBLISHED_UNITS = "changeVsPublishedPublishedUnits"

/** Sales vs published expected demand over the last `weeks`: the rule fields and the units behind them. */
export const salesVsExpectedFields = (weeks: number) => ({
  signed: `salesVsExpected${weeks}wPct`,
  abs: `salesVsExpected${weeks}wAbsPct`,
  units: `salesVsExpected${weeks}wUnits`,
  absUnits: `salesVsExpected${weeks}wAbsUnits`,
  sales: `salesVsExpected${weeks}wSales`,
  expected: `salesVsExpected${weeks}wExpected`,
})

const unitsOf = (row: Record<string, unknown>, key: string) =>
  typeof row[key] === "number" ? formatNumber(row[key]) : null

const changeUnits = (row: Record<string, unknown>) => {
  const draft = unitsOf(row, CHANGE_DRAFT_UNITS)
  const published = unitsOf(row, CHANGE_PUBLISHED_UNITS)
  return draft && published ? `${draft} draft vs ${published} published` : undefined
}

const salesVsExpectedUnits = (weeks: number) => (row: Record<string, unknown>) => {
  const keys = salesVsExpectedFields(weeks)
  const sales = unitsOf(row, keys.sales)
  const expected = unitsOf(row, keys.expected)
  return sales && expected
    ? `${sales} ${formatName("sale", true)} vs ${expected} expected`
    : undefined
}

/**
 * A field only rules read: no variant read carries it, so a table stamps it onto its rows. `scope`
 * says who stamps it: the rule evaluation itself ("all"), or the Demand page (which holds the draft
 * and worklist).
 */
export type VariantRuleField = FilterFieldType & {
  scope: "all" | "demand"
  /** How the Rules hover shows the variant's value; plain text when absent. */
  formatValue?: (value: number) => string
  /** The units behind the value, from fields stamped beside it, for the Rules hover. */
  describe?: (row: Record<string, unknown>) => string | undefined
}

/** A demand comparison as a signed and an up-or-down field, each in % and in units. */
const comparisonFields = ({
  name,
  pct: [pct, absPct],
  units: [units, absUnits],
  describe,
}: {
  name: string
  pct: [string, string]
  units: [string, string]
  describe: VariantRuleField["describe"]
}): VariantRuleField[] =>
  [
    { value: pct, name: `${name} (%)`, icon: <Percent />, signed: true, digits: 1, suffix: "%" },
    {
      value: absPct,
      name: `${name}, up or down (%)`,
      icon: <ArrowsDownUp />,
      signed: false,
      digits: 1,
      suffix: "%",
    },
    { value: units, name: `${name} (units)`, icon: <Hash />, signed: true, digits: 0, suffix: "" },
    {
      value: absUnits,
      name: `${name}, up or down (units)`,
      icon: <ArrowsDownUp />,
      signed: false,
      digits: 0,
      suffix: "",
    },
  ].map(({ signed, digits, suffix, ...field }) => ({
    ...field,
    type: "number",
    unit: suffix || "units",
    scope: "demand",
    formatValue: (value: number) =>
      `${signed ? formatNumberWithSign(value, digits) : formatNumber(value, digits)}${suffix}`,
    describe,
  }))

export const VARIANT_RULE_FIELDS: readonly VariantRuleField[] = [
  {
    value: FORECAST_QUALITY_FIELD,
    name: "Forecast quality",
    type: "enum",
    icon: <Gauge />,
    options: Object.values(FORECAST_QUALITY_LABELS),
    scope: "all",
  },
  ...comparisonFields({
    name: "Change vs published",
    pct: [CHANGE_VS_PUBLISHED_FIELD, CHANGE_VS_PUBLISHED_ABS_FIELD],
    units: [CHANGE_VS_PUBLISHED_UNITS_FIELD, CHANGE_VS_PUBLISHED_ABS_UNITS_FIELD],
    describe: changeUnits,
  }),
  // NOOS offers every status; fashion a subset.
  { ...matchStatusField("noos"), scope: "demand" },
  ...DEMAND2_PERF_WINDOWS.flatMap((weeks) => {
    const keys = salesVsExpectedFields(weeks)
    return comparisonFields({
      // eslint-disable-next-line @hakio/no-untranslated-terminology
      name: `Sales vs expected demand, last ${weeks} weeks`,
      pct: [keys.signed, keys.abs],
      units: [keys.units, keys.absUnits],
      describe: salesVsExpectedUnits(weeks),
    })
  }),
  {
    value: PERFORMANCE_STATUS_FIELD,
    name: `Performance, last ${PERFORMANCE_STATUS_WEEKS} weeks`,
    type: "enum",
    icon: <ChartLineUp />,
    options: PERFORMANCE_STATUSES.map((d) => d.status),
    scope: "demand",
    describe: salesVsExpectedUnits(PERFORMANCE_STATUS_WEEKS),
  },
]

/** The Demand-only fields, behind their own row in the rule's filter editor. */
export const DEMAND_RULE_FIELD_GROUP: FilterFieldGroup = {
  label: "Demand filters",
  icon: <ChartLineUp />,
  fields: VARIANT_RULE_FIELDS.filter((f) => f.scope === "demand"),
}

/** For the filter editor, which takes the rule-only fields on top of the page filter fields. */
export const variantRuleFilterFields: FilterFieldType[] = [...VARIANT_RULE_FIELDS]

export const VARIANT_RULE_FIELD_BY_KEY: ReadonlyMap<string, VariantRuleField> = new Map(
  VARIANT_RULE_FIELDS.map((f) => [f.value, f]),
)

export const RULE_ONLY_FIELDS: ReadonlySet<string> = new Set(VARIANT_RULE_FIELD_BY_KEY.keys())

/** The rule-only fields Demand's Total view stamps when its shown Rules column needs them. */
export const DEMAND_RULE_FIELDS: ReadonlySet<string> = new Set(
  VARIANT_RULE_FIELDS.filter((f) => f.scope === "demand").map((f) => f.value),
)
